import React from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SegmentResponseMapData } from '../experimentTypes';
import { EXPERIMENTS_TEST_IDS } from '../experimentsTestIds';

interface SegmentResponseMapProps {
  experimentId: string;
  data: SegmentResponseMapData;
}

function formatRate(rate: number | null): string {
  return rate === null ? '—' : `${(rate * 100).toFixed(1)}%`;
}

/**
 * Discovery experiment output: segment × variant response matrix.
 * Pre-registered segments are confirmatory; anything else is exploratory.
 * Low-denominator cells arrive suppressed from the backend and render as such.
 */
export const SegmentResponseMap: React.FC<SegmentResponseMapProps> = ({ experimentId, data }) => {
  const segments = Array.from(new Set(data.cells.map(cell => cell.segmentLabel)));
  const variants = Array.from(new Set(data.cells.map(cell => cell.variantId)));
  const cellFor = (segment: string, variant: string) =>
    data.cells.find(cell => cell.segmentLabel === segment && cell.variantId === variant);

  return (
    <Box data-testid={EXPERIMENTS_TEST_IDS.segmentResponseMap(experimentId)}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Segment response on {data.metric}. Cells show rate (numerator / denominator); suppressed cells lack enough users for a trustworthy rate.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Segment</TableCell>
            {variants.map(variant => (
              <TableCell key={variant} align="right">{variant}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {segments.map(segment => {
            const anyCell = data.cells.find(cell => cell.segmentLabel === segment);
            return (
              <TableRow key={segment}>
                <TableCell>
                  <Typography variant="body2" component="span">{segment}</Typography>{' '}
                  {anyCell && (
                    <Chip
                      size="small"
                      label={anyCell.preRegistered ? 'confirmatory' : 'exploratory'}
                      color={anyCell.preRegistered ? 'primary' : 'default'}
                      sx={{ height: 18, fontSize: 10 }}
                    />
                  )}
                </TableCell>
                {variants.map(variant => {
                  const cell = cellFor(segment, variant);
                  return (
                    <TableCell
                      key={variant}
                      align="right"
                      data-testid={EXPERIMENTS_TEST_IDS.segmentResponseCell(segment, variant)}
                    >
                      {!cell ? '—' : cell.suppressed ? (
                        <Tooltip title={`Suppressed: only ${cell.denominator} users in this cell`}>
                          <Typography variant="body2" color="text.disabled" component="span">
                            suppressed ({cell.numerator} / {cell.denominator})
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" component="span">
                          {formatRate(cell.rate)} ({cell.numerator} / {cell.denominator})
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};
